import React, { createContext, useState, useContext, useEffect } from 'react';
import { auth } from '../firebase/config';
import { onAuthStateChanged, signOut } from 'firebase/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check for admin session in sessionStorage on mount (completely separate from Firebase)
  // Using sessionStorage so session expires when tab is closed
  useEffect(() => {
    const checkAdminSession = () => {
      const adminSession = sessionStorage.getItem('adminSession');
      const sessionTimestamp = sessionStorage.getItem('adminSessionTimestamp');
      
      if (adminSession === 'true' && sessionTimestamp) {
        const now = Date.now();
        const timestamp = parseInt(sessionTimestamp, 10);
        // Check if session is still valid (within 24 hours)
        if (now - timestamp < 24 * 60 * 60 * 1000) {
          setIsAdmin(true);
        } else {
          // Session expired, clear it
          sessionStorage.removeItem('adminSession');
          sessionStorage.removeItem('adminSessionTimestamp');
          sessionStorage.removeItem('adminSessionTabId');
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    };
    
    checkAdminSession();
  }, []);

  // Listen for Firebase authentication state changes (only for regular users, not admin)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      
      // Admin session is completely separate - don't interfere with it
      // Check admin session again to ensure it's preserved
      const adminSession = sessionStorage.getItem('adminSession');
      const sessionTimestamp = sessionStorage.getItem('adminSessionTimestamp');
      
      if (adminSession === 'true' && sessionTimestamp) {
        const now = Date.now();
        const timestamp = parseInt(sessionTimestamp, 10);
        // Only update isAdmin if session is valid
        if (now - timestamp < 24 * 60 * 60 * 1000) {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
      } else {
        // Only set isAdmin to false if there's no admin session
        // Don't override if admin session exists
        if (adminSession !== 'true') {
          setIsAdmin(false);
        }
      }
      
      if (user) {
        console.log('User is logged in:', user.email);
      } else {
        console.log('User is logged out');
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Function to set admin session (for hardcoded admin login - no Firebase)
  // Using sessionStorage so session expires when tab is closed
  const setAdminSession = (status) => {
    setIsAdmin(status);
    if (status) {
      // Get or create tab ID for this session
      let tabId = sessionStorage.getItem('adminTabId');
      if (!tabId) {
        tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('adminTabId', tabId);
      }
      
      sessionStorage.setItem('adminSession', 'true');
      // Store timestamp to verify session validity
      sessionStorage.setItem('adminSessionTimestamp', Date.now().toString());
      // Store tab ID to ensure session is from same tab
      sessionStorage.setItem('adminSessionTabId', tabId);
    } else {
      sessionStorage.removeItem('adminSession');
      sessionStorage.removeItem('adminSessionTimestamp');
      sessionStorage.removeItem('adminSessionTabId');
    }
  };

  const logout = async () => {
    try {
      // Check if it's an admin session
      const adminSession = sessionStorage.getItem('adminSession');
      if (adminSession === 'true') {
        // Admin logout - clear sessionStorage only, no Firebase
        setAdminSession(false);
        sessionStorage.removeItem('adminSession');
        sessionStorage.removeItem('adminSessionTimestamp');
        sessionStorage.removeItem('adminSessionTabId');
      } else {
        // Regular user logout - use Firebase
        await signOut(auth);
        setCurrentUser(null);
        setIsAdmin(false);
      }
      console.log('User logged out successfully');
    } catch (error) {
      console.error('Error logging out:', error);
      throw error;
    }
  };

  const value = {
    currentUser,
    loading,
    logout,
    isLoggedIn: !!currentUser,
    isAdmin,
    setAdminSession
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

